import { ContractPriceChangeLog, ContractPriceDerivedCreated } from '../../lib/api';
import { usePreferences } from '../../contexts/PreferencesContext';
import { Pagination } from '../ui/pagination';
import { Edit, FilePlus2, History, User } from 'lucide-react';
// CR-5973: Audit-trail formatting helpers live in their own file so the
// detail modal stays under the 500-line cap.
import {
  HISTORY_FIELD_ICONS,
  getHistoryFieldLabel,
  humaniseHistoryValue,
} from './contractHistoryFormat';

interface ContractPriceHistoryTabProps {
  logs: ContractPriceChangeLog[];
  /** Total audit-trail entries on the server, not just this page. */
  totalCount: number;
  /**
   * Customer review 11 Sep (item 21): "Created by <user> on <date>", read
   * from the contract when its trail has no creation row. Shown as the oldest
   * entry — on the last page, or alone when the trail is empty.
   */
  derivedCreated?: ContractPriceDerivedCreated | null;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

/**
 * CR-5973 layout, extracted for CR-6424 (R2): the contract audit trail with
 * real pagination.
 *
 * The modal used to fetch page 1 only and print that page's length as the
 * total, so any trail past the server page size (20) silently vanished — a
 * 25-part import writes 26 entries on day one. The pager walks the full
 * trail and the tab label shows the server's count.
 *
 * @link ContractPriceDetailModal owns the fetch; this renders one page.
 */
export default function ContractPriceHistoryTab({
  logs,
  totalCount,
  derivedCreated = null,
  currentPage,
  pageSize,
  onPageChange,
}: ContractPriceHistoryTabProps) {
  const { formatDate, formatDateTime } = usePreferences();
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const showDerived = Boolean(derivedCreated) && currentPage >= totalPages;

  if (totalCount === 0 && !derivedCreated) {
    return (
      <div className="text-center py-12 bg-muted/30 rounded-lg">
        <History className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground">No change history available</p>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-3">
        {logs.map((log) => {
          const FieldIcon = HISTORY_FIELD_ICONS[log.field_changed] || Edit;
          // 'contract' is the Pricing import's "Created via import" row.
          const isCreation = log.field_changed === 'created' || log.field_changed === 'contract';
          return (
            <div key={log.id} className="border rounded-lg p-3 bg-white shadow-sm">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <FieldIcon className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="font-medium text-sm">{getHistoryFieldLabel(log.field_changed)}</span>
                  {log.contract_part_number && (
                    <span className="text-xs text-muted-foreground">(Part: {log.contract_part_number})</span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground text-right">
                  {formatDateTime(log.changed_at)}
                </div>
              </div>
              <div className="text-sm break-words">
                {isCreation ? (
                  // CR-5973: Backend writes the creation row as a multi-line
                  // snapshot of the contract's key initial values. Preserve
                  // newlines so the baseline reads cleanly.
                  <span className="font-medium whitespace-pre-wrap">{log.new_value || 'Contract created'}</span>
                ) : (
                  <>
                    <span className="text-muted-foreground line-through">
                      {humaniseHistoryValue(log.field_changed, log.old_value, formatDate) || '(empty)'}
                    </span>
                    <span className="mx-2 text-muted-foreground">&rarr;</span>
                    <span className="font-medium">
                      {humaniseHistoryValue(log.field_changed, log.new_value, formatDate) || '(empty)'}
                    </span>
                  </>
                )}
              </div>
              <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                <User className="h-3 w-3" />
                {log.changed_by_name || 'Unknown'}
              </div>
            </div>
          );
        })}
        {showDerived && derivedCreated && (
          <div className="border border-dashed rounded-lg p-3 bg-muted/20" data-testid="contract-log-derived-created">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <FilePlus2 className="h-4 w-4 text-primary flex-shrink-0" />
                <span className="font-medium text-sm">Contract Created</span>
              </div>
              <div className="text-xs text-muted-foreground text-right">
                {derivedCreated.changed_at ? formatDateTime(derivedCreated.changed_at) : ''}
              </div>
            </div>
            <div className="text-sm font-medium">
              Created by {derivedCreated.changed_by_name} on {derivedCreated.changed_at ? formatDate(derivedCreated.changed_at) : 'an unknown date'}
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              Taken from the contract record — this contract was loaded in bulk and no creation log was written at the time.
            </div>
          </div>
        )}
      </div>
      {totalPages > 1 && (
        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            itemsPerPage={pageSize}
            onPageChange={onPageChange}
            itemLabel="log entries"
          />
        </div>
      )}
    </div>
  );
}
